// The schedule board's swim lanes (spec 03 §4.6; design-system §3.16): one lane
// per work centre over a window of facility dates, its closed time shaded, each
// stretch below full rate hatched and labelled ("Night shift · 70%"), each placed
// operation a block from its start to its end with its changeover drawn as a
// lead-in, its closed time hatched, its batch outlined with the setup key and
// its glyphs, unplaced work on a warning row under the lane, and each open
// overload a red marker on its week. A pattern of existing primitives: plain
// positioned boxes and inline SVG, no Gantt or charting dependency (D-406
// ruling 3). On a phone the board renders `ScheduleCards` instead.
//
// The drag (P3-I-3, §3.6): a holder of `planning.schedule.publish` drags a
// block along its lane or onto a lane its step may run on, the others dimmed
// while it moves; the drop asks the board for a preview, whose ghost blocks
// the lanes then draw. Pointer events serve a mouse, a pen and a finger alike;
// the block drawer's move form is the keyboard's way to the same preview.

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../../components/status-chip";
import type { ScheduleLane, ScheduleOperation, ScheduleOverload } from "../hooks/schedule-queries";
import { formatExactQty } from "../lib/mrp-lib";
import {
  closedGaps,
  dayTicks,
  drawnInterval,
  isSlowPiece,
  keyText,
  labelFromEnd,
  minSpanMs,
  pieceLabel,
  spanOf,
  stackRows,
  wallMinutesOf,
  windowUtilization,
  zoneOffset,
  zonedStamp,
  type TimeWindow,
} from "../lib/schedule-lib";
import { formatDate, weekLabel } from "../lib/planning-lib";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { wallToUtcMs } from "../lib/calendar-lib";
import {
  draggedStart,
  laneAllowed,
  laneAt,
  moveBlock,
  type Ghost,
  type MoveBlock,
} from "../lib/schedule-moves-lib";
import { UnplacedReasonText } from "./schedule-bits";

/** The hatch and the late stripe, defined once per board and filled by reference. */
export function ScheduleHatchDefs() {
  return (
    <svg className="planning-svg-defs" aria-hidden="true" focusable="false" width="0" height="0">
      <defs>
        <pattern
          id="planning-hatch"
          patternUnits="userSpaceOnUse"
          width="6"
          height="6"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="6" className="planning-hatch-line" />
        </pattern>
        <pattern
          id="planning-late-stripe"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="8" className="planning-late-line" />
        </pattern>
      </defs>
    </svg>
  );
}

/** A hatched box filling its parent's span, for closed time, a slow piece or proposed load. */
export function Hatch({
  left,
  width,
  late = false,
  className,
  testId,
}: {
  left: number;
  width: number;
  late?: boolean;
  className?: string;
  testId?: string;
}) {
  return (
    <svg
      className={className ?? "planning-hatched"}
      aria-hidden="true"
      focusable="false"
      preserveAspectRatio="none"
      style={{ left: `${String(left)}%`, width: `${String(width)}%` }}
      data-testid={testId}
    >
      <rect
        width="100%"
        height="100%"
        fill={late ? "url(#planning-late-stripe)" : "url(#planning-hatch)"}
      />
    </svg>
  );
}

function PinGlyph() {
  return (
    <svg viewBox="0 0 12 12" className="planning-glyph" aria-hidden="true" focusable="false">
      <path d="M6 1v4M3.5 5h5L7.5 7.5h-3zM6 7.5V11" />
    </svg>
  );
}

function LockGlyph() {
  return (
    <svg viewBox="0 0 12 12" className="planning-glyph" aria-hidden="true" focusable="false">
      <path d="M3 5.5h6v5H3zM4.3 5.5V4a1.7 1.7 0 0 1 3.4 0v1.5" />
    </svg>
  );
}

function FenceGlyph() {
  return (
    <svg viewBox="0 0 12 12" className="planning-glyph" aria-hidden="true" focusable="false">
      <path d="M2 2v8M6 2v8M10 2v8M1 5h10M1 8h10" />
    </svg>
  );
}

/** The glyphs and chips an operation wears: in progress, pinned, fenced, late, released pin. */
export function OperationMarks({
  op,
  compact = false,
}: {
  op: ScheduleOperation;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const marks: ReactNode[] = [];
  if (op.fixed === "in_progress") {
    marks.push(
      <span key="lock" className="planning-mark" title={t("planning.schedule.block.inProgress")}>
        <LockGlyph />
        {compact ? null : <span>{t("planning.schedule.block.inProgress")}</span>}
      </span>,
    );
  }
  if (op.fixed === "pin_position") {
    marks.push(
      <span key="pin" className="planning-mark" title={t("planning.schedule.block.pinned")}>
        <PinGlyph />
        {compact ? null : <span>{t("planning.schedule.block.pinned")}</span>}
      </span>,
    );
  }
  if (op.fixed === "fenced") {
    marks.push(
      <span key="fence" className="planning-mark" title={t("planning.schedule.block.fenced")}>
        <FenceGlyph />
        {compact ? null : <span>{t("planning.schedule.block.fencedShort")}</span>}
      </span>,
    );
  }
  if (op.is_late) {
    marks.push(
      <StatusChip key="late" variant="bad" data-testid="planning-schedule-late-chip">
        {t("planning.schedule.block.late")}
      </StatusChip>,
    );
  }
  if (marks.length === 0) return null;
  return <span className="planning-marks">{marks}</span>;
}

/** The data attributes every block and card carries, so a walkthrough reads facts, not words. */
export function opData(op: ScheduleOperation): Record<string, string> {
  const wall = wallMinutesOf(op.planned_start, op.planned_end);
  return {
    "data-op-ref": op.op_ref,
    "data-order-no": op.label.order_no ?? "",
    "data-kind": op.source_kind,
    "data-work-center-id": op.work_center_id,
    "data-sequence": op.sequence_index === null ? "" : String(op.sequence_index),
    "data-start": op.planned_start ?? "",
    "data-end": op.planned_end ?? "",
    "data-wall-minutes": wall === undefined ? "" : String(wall),
    "data-setup-minutes": String(op.setup_minutes),
    "data-run-minutes": String(op.run_minutes),
    "data-batch-key": op.batch_key ?? "",
    "data-setup-key": op.setup_key ?? "",
    "data-late": String(op.is_late),
    "data-fixed": op.fixed ?? "",
    "data-placement": op.placement,
  };
}

/** The board's drag (§3.6): who may drag, and where a drop or a refusal goes. */
export interface LanesDrag {
  readonly onDrop: (op: ScheduleOperation, startMs: number, laneId: string) => void;
  /** A block that cannot move was dragged, or one was dropped on a lane its step cannot use. */
  readonly onRefused: (op: ScheduleOperation, reason: MoveBlock | "lane", laneId: string) => void;
}

export interface LanesProps {
  readonly lanes: readonly ScheduleLane[];
  readonly operations: readonly ScheduleOperation[];
  readonly unplaced: readonly ScheduleOperation[];
  readonly overloads: readonly ScheduleOverload[];
  readonly window: TimeWindow;
  readonly timezone: string;
  readonly zoom: "day" | "week";
  readonly onOpen: (op: ScheduleOperation) => void;
  readonly onOverload: (overload: ScheduleOverload) => void;
  /** Present for a reader who may drag; absent, the blocks only open. */
  readonly drag?: LanesDrag | undefined;
  /** A preview's ghost blocks, each on its new lane at its new start. */
  readonly ghosts?: readonly Ghost[] | undefined;
  /** The facility's shift names by code, so a slow piece reads "Night shift · 70%". */
  readonly shiftNames?: ReadonlyMap<string, string> | undefined;
}

/** A drag in flight: where it started and where it would land. */
interface DragState {
  readonly op: ScheduleOperation;
  readonly startMs: number;
  readonly endMs: number;
  readonly targetStartMs: number;
  readonly targetLane: string;
}

interface DragOrigin {
  readonly op: ScheduleOperation;
  readonly startMs: number;
  readonly endMs: number;
  readonly x: number;
  readonly y: number;
  readonly trackWidth: number;
  readonly bands: readonly { id: string; top: number; bottom: number }[];
  readonly blocked: MoveBlock | undefined;
  moved: boolean;
}

/** Pixels the pointer travels before a press becomes a drag, so a click still opens. */
const DRAG_THRESHOLD_PX = 5;

/** The pixels per hour at the day zoom; the week zoom fits the window to the page. */
const DAY_ZOOM_PX_PER_HOUR = 14;

export function ScheduleLanes({
  lanes,
  operations,
  unplaced,
  overloads,
  window,
  timezone,
  zoom,
  onOpen,
  onOverload,
  drag,
  ghosts = [],
  shiftNames,
}: LanesProps) {
  const { t, i18n } = useTranslation();
  const ticks = useMemo(() => dayTicks(window, timezone), [window, timezone]);
  const hours = (window.endMs - window.startMs) / 3_600_000;
  const minWidth = zoom === "day" ? Math.round(hours * DAY_ZOOM_PX_PER_HOUR) : undefined;
  const lanesRef = useRef<HTMLDivElement>(null);
  // The track's width in pixels, so a block's 24 px floor can be turned into
  // time: every lane's track is the axis track's width. Measured before paint
  // and again on each resize; 0 (no floor beyond the CSS min-width) until then.
  const axisRef = useRef<HTMLDivElement>(null);
  const [trackPx, setTrackPx] = useState(0);
  useLayoutEffect(() => {
    const el = axisRef.current;
    if (el === null) return;
    const measure = () => {
      setTrackPx(Math.round(el.getBoundingClientRect().width));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);
  const minMs = minSpanMs(trackPx, window);
  const origin = useRef<DragOrigin | undefined>(undefined);
  // A drag ends in a pointerup and then a click on the same block; the click
  // must not open the drawer the drag never meant to open.
  const swallowClick = useRef(false);
  // The drag's latest target, read by the release without a state updater.
  const latest = useRef<DragState | undefined>(undefined);
  const [dragging, setDragging] = useState<DragState | undefined>(undefined);
  const moving = useMemo(() => new Set(ghosts.map((g) => g.op_ref)), [ghosts]);

  useEffect(() => {
    if (dragging === undefined) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        origin.current = undefined;
        latest.current = undefined;
        // The press still ends in a release and a click; neither opens anything.
        swallowClick.current = true;
        setDragging(undefined);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [dragging]);

  const pointer = drag
    ? {
        down(
          event: PointerEvent<HTMLElement>,
          op: ScheduleOperation,
          startMs: number,
          endMs: number,
        ) {
          if (event.button !== 0) return;
          const track = event.currentTarget.parentElement;
          const bands = [
            ...(lanesRef.current?.querySelectorAll<HTMLElement>(
              '[data-testid="planning-schedule-lane"]',
            ) ?? []),
          ].map((el) => {
            const r = el.getBoundingClientRect();
            return { id: el.dataset["workCenterId"] ?? "", top: r.top, bottom: r.bottom };
          });
          origin.current = {
            op,
            startMs,
            endMs,
            x: event.clientX,
            y: event.clientY,
            trackWidth: track?.getBoundingClientRect().width ?? 0,
            bands,
            blocked: moveBlock(op),
            moved: false,
          };
          // Capture keeps the moves coming to the block when the pointer leaves it.
          if ("setPointerCapture" in event.currentTarget) {
            event.currentTarget.setPointerCapture(event.pointerId);
          }
        },
        move(event: PointerEvent<HTMLElement>) {
          const o = origin.current;
          if (o === undefined) return;
          const dx = event.clientX - o.x;
          const dy = event.clientY - o.y;
          if (!o.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
          if (o.blocked !== undefined) {
            origin.current = undefined;
            swallowClick.current = true;
            drag.onRefused(o.op, o.blocked, o.op.work_center_id);
            return;
          }
          o.moved = true;
          const next: DragState = {
            op: o.op,
            startMs: o.startMs,
            endMs: o.endMs,
            targetStartMs: draggedStart(o.startMs, dx, o.trackWidth, window),
            targetLane: laneAt(event.clientY, o.bands) ?? o.op.work_center_id,
          };
          latest.current = next;
          setDragging(next);
        },
        up() {
          const o = origin.current;
          const current = latest.current;
          origin.current = undefined;
          latest.current = undefined;
          if (!o?.moved) return;
          swallowClick.current = true;
          setDragging(undefined);
          if (current === undefined) return;
          const lane = current.targetLane;
          if (!laneAllowed(current.op, lane)) {
            drag.onRefused(current.op, "lane", lane);
          } else if (
            lane !== current.op.work_center_id ||
            current.targetStartMs !== current.startMs
          ) {
            drag.onDrop(current.op, current.targetStartMs, lane);
          }
        },
        cancel() {
          origin.current = undefined;
          latest.current = undefined;
          setDragging(undefined);
        },
        click(op: ScheduleOperation) {
          if (swallowClick.current) {
            swallowClick.current = false;
            return;
          }
          onOpen(op);
        },
      }
    : undefined;

  // The zone line sits under the bordered, scrolling lanes rather than inside
  // them (D-450): inside, it scrolled with the day zoom and read as part of the
  // chart.
  return (
    <>
      <div
        className={
          dragging !== undefined ? "planning-lanes-scroll is-dragging" : "planning-lanes-scroll"
        }
        data-testid="planning-schedule-lanes"
        data-zoom={zoom}
        data-dragging={dragging !== undefined ? dragging.op.op_ref : ""}
      >
        <ScheduleHatchDefs />
        <div
          ref={lanesRef}
          className="planning-lanes"
          style={minWidth !== undefined ? { minWidth: `${String(minWidth)}px` } : undefined}
        >
          <div className="planning-lane planning-lane-axis" aria-hidden="true">
            <div className="planning-lane-head" />
            <div ref={axisRef} className="planning-lane-track planning-axis-track">
              {ticks.map((tick) => (
                <span
                  key={tick.date}
                  className="planning-axis-tick"
                  style={{ left: `${String(tick.left)}%`, width: `${String(tick.width)}%` }}
                >
                  {formatDate(tick.date, i18n.language)}
                </span>
              ))}
            </div>
          </div>
          {lanes.map((lane) => (
            <Lane
              key={lane.work_center_id}
              lane={lane}
              ops={operations.filter((o) => o.work_center_id === lane.work_center_id)}
              unplaced={unplaced.filter((o) => o.work_center_id === lane.work_center_id)}
              overloads={overloads.filter((o) => o.work_center_id === lane.work_center_id)}
              ticks={ticks}
              window={window}
              minMs={minMs}
              trackPx={trackPx}
              timezone={timezone}
              onOpen={
                pointer !== undefined
                  ? (op: ScheduleOperation) => {
                      pointer.click(op);
                    }
                  : onOpen
              }
              onOverload={onOverload}
              shiftNames={shiftNames}
              pointer={pointer}
              dragging={dragging}
              ghosts={ghosts.filter((g) => g.laneId === lane.work_center_id)}
              ghostOps={operations}
              moving={moving}
            />
          ))}
        </div>
      </div>
      <p className="planning-hint planning-schedule-zone" data-testid="planning-schedule-zone">
        {t("planning.schedule.zoneNote", {
          zone: timezone,
          offset: zoneOffset(window.startMs, timezone),
        })}
      </p>
    </>
  );
}

/** The pointer handlers a draggable block wears. */
interface BlockPointer {
  down(
    event: PointerEvent<HTMLElement>,
    op: ScheduleOperation,
    startMs: number,
    endMs: number,
  ): void;
  move(event: PointerEvent<HTMLElement>): void;
  up(): void;
  cancel(): void;
}

function Lane({
  lane,
  ops,
  unplaced,
  overloads,
  ticks,
  window,
  minMs,
  trackPx,
  timezone,
  onOpen,
  onOverload,
  shiftNames,
  pointer,
  dragging,
  ghosts,
  ghostOps,
  moving,
}: {
  lane: ScheduleLane;
  ops: readonly ScheduleOperation[];
  unplaced: readonly ScheduleOperation[];
  overloads: readonly ScheduleOverload[];
  ticks: readonly { date: string; left: number; width: number }[];
  window: TimeWindow;
  /** The block floor in time on this track (`minSpanMs`); 0 before the track is measured. */
  minMs: number;
  /** The track's measured width in pixels; 0 before it is measured. */
  trackPx: number;
  timezone: string;
  shiftNames: ReadonlyMap<string, string> | undefined;
  onOpen: (op: ScheduleOperation) => void;
  onOverload: (overload: ScheduleOverload) => void;
  pointer: BlockPointer | undefined;
  dragging: DragState | undefined;
  ghosts: readonly Ghost[];
  ghostOps: readonly ScheduleOperation[];
  moving: ReadonlySet<string>;
}) {
  const { t, i18n } = useTranslation();
  const timed = ops
    .filter((o) => o.planned_start !== null && o.planned_end !== null)
    .map((o) => ({
      op: o,
      op_ref: o.op_ref,
      startMs: Date.parse(o.planned_start ?? ""),
      endMs: Date.parse(o.planned_end ?? ""),
    }));
  // Each block where it is drawn: no narrower than the floor and inside the
  // window. Rows stack on the drawn interval, so two short operations drawn
  // at the floor sit on two rows instead of one covering the other.
  const drawn = new Map(
    timed.map((o) => [o.op_ref, drawnInterval(o.startMs, o.endMs, window, minMs)] as const),
  );
  const { rows, count } = stackRows(
    timed.map((o) => {
      const d = drawn.get(o.op_ref);
      return d === undefined ? o : { op_ref: o.op_ref, startMs: d.startMs, endMs: d.endMs };
    }),
  );
  const util = windowUtilization(timed, lane.pieces, window);
  const unbound = lane.availability.source === "unbound";
  const allowed = dragging === undefined || laneAllowed(dragging.op, lane.work_center_id);
  const target = dragging?.targetLane === lane.work_center_id;
  const laneClasses = ["planning-lane"];
  if (!allowed) laneClasses.push("is-dimmed");
  if (target && allowed) laneClasses.push("is-drop-target");
  const dragSpan =
    dragging !== undefined && target
      ? spanOf(
          dragging.targetStartMs,
          dragging.targetStartMs + (dragging.endMs - dragging.startMs),
          window,
        )
      : undefined;
  const ghostOf = new Map(ghostOps.map((o) => [o.op_ref, o]));
  return (
    <div
      className={laneClasses.join(" ")}
      data-testid="planning-schedule-lane"
      data-work-center-id={lane.work_center_id}
      data-code={lane.code}
      data-drop={dragging === undefined ? "" : allowed ? "allowed" : "not-allowed"}
    >
      <div className="planning-lane-head" title={t("planning.schedule.lane.tooltip")}>
        <span className="planning-lane-name" data-testid="planning-schedule-lane-name">
          {lane.name.length > 0 && lane.name !== lane.code
            ? `${lane.code} · ${lane.name}`
            : lane.code}
        </span>
        <span
          className="planning-hint"
          data-testid="planning-schedule-lane-util"
          data-pct={util === undefined ? "" : String(util)}
        >
          {util === undefined
            ? t("planning.schedule.lane.noCapacity")
            : t("planning.schedule.lane.utilization", {
                pct: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(
                  util,
                ),
              })}
        </span>
        <span
          className="planning-hint"
          data-testid="planning-schedule-lane-availability"
          data-source={lane.availability.source}
          data-factor={lane.availability.factor}
        >
          {unbound
            ? t("planning.schedule.lane.availabilityUnbound")
            : t("planning.schedule.lane.availability", {
                pct: Math.round(Number(lane.availability.factor) * 100),
              })}
        </span>
        {lane.gate.hard_blocked ? (
          <StatusChip variant="bad" data-testid="planning-schedule-lane-blocked">
            {t("planning.schedule.lane.blocked")}
          </StatusChip>
        ) : null}
      </div>
      <div
        className="planning-lane-track"
        style={{ height: `${String(count * 46 + 10)}px` }}
        data-testid="planning-schedule-track"
      >
        {ticks.map((tick) => (
          <span
            key={tick.date}
            className="planning-lane-daymark"
            style={{ left: `${String(tick.left)}%` }}
            aria-hidden="true"
          />
        ))}
        {lane.pieces.map((piece) => {
          const span = spanOf(Date.parse(piece.start), Date.parse(piece.end), window);
          if (span === undefined) return null;
          const slow = isSlowPiece(piece);
          const classes = slow ? ["planning-piece", "planning-piece-slow"] : ["planning-piece"];
          if (slow && labelFromEnd(span.left, trackPx)) classes.push("is-label-end");
          return (
            <div
              key={piece.start}
              className={classes.join(" ")}
              style={{ left: `${String(span.left)}%`, width: `${String(span.width)}%` }}
              data-testid={slow ? "planning-schedule-piece-slow" : "planning-schedule-piece"}
              data-label={pieceLabel(piece, shiftNames)}
              data-shift-code={piece.shift_code ?? ""}
              data-start={piece.start}
              data-end={piece.end}
            >
              {slow ? (
                <>
                  <Hatch left={0} width={100} />
                  <span className="planning-piece-label">{pieceLabel(piece, shiftNames)}</span>
                </>
              ) : null}
            </div>
          );
        })}
        {overloads.map((overload) => {
          const startMs = wallToUtcMs(overload.week_start, "00:00", timezone);
          const endMs = wallToUtcMs(addDaysIso(overload.week_start, 7), "00:00", timezone);
          const span = spanOf(startMs, endMs, window);
          if (span === undefined) return null;
          return (
            <button
              key={overload.id}
              type="button"
              className="planning-overload-marker"
              style={{ left: `${String(span.left)}%`, width: `${String(span.width)}%` }}
              data-testid="planning-schedule-overload"
              data-week={overload.week_start}
              data-hours={overload.overload_hours}
              aria-label={t("planning.schedule.overload.aria", {
                hours: overload.overload_hours,
                week: weekLabel(overload.week_start, i18n.language),
              })}
              onClick={() => {
                onOverload(overload);
              }}
            >
              <span>
                {t("planning.schedule.overload.marker", { hours: overload.overload_hours })}
              </span>
            </button>
          );
        })}
        {timed.map(({ op, startMs, endMs }) => {
          const d = drawn.get(op.op_ref);
          const span = d === undefined ? undefined : spanOf(d.startMs, d.endMs, window);
          if (span === undefined) return null;
          return (
            <Block
              key={op.op_ref}
              op={op}
              startMs={startMs}
              endMs={endMs}
              span={span}
              row={rows.get(op.op_ref) ?? 0}
              lane={lane}
              timezone={timezone}
              onOpen={onOpen}
              pointer={pointer}
              dragged={dragging?.op.op_ref === op.op_ref}
              moving={moving.has(op.op_ref)}
            />
          );
        })}
        {ghosts.map((g) => {
          const span = spanOf(g.startMs, g.endMs, window);
          if (span === undefined) return null;
          const op = ghostOf.get(g.op_ref);
          const order = op?.label.order_no ?? t("planning.schedule.block.plannedOrder");
          const start = zonedStamp(new Date(g.startMs).toISOString(), i18n.language, timezone);
          const end = zonedStamp(new Date(g.endMs).toISOString(), i18n.language, timezone);
          return (
            <div
              key={`ghost-${g.op_ref}`}
              className={g.primary ? "planning-block-ghost is-primary" : "planning-block-ghost"}
              style={{ left: `${String(span.left)}%`, width: `${String(span.width)}%` }}
              data-testid="planning-schedule-ghost"
              data-op-ref={g.op_ref}
              data-start={new Date(g.startMs).toISOString()}
              data-primary={String(g.primary)}
              role="img"
              aria-label={t("planning.schedule.move.ghost", { order, start, end })}
            >
              <span className="planning-block-title">{order}</span>
            </div>
          );
        })}
        {dragging !== undefined && dragSpan !== undefined && allowed ? (
          <div
            className="planning-block-ghost is-live"
            style={{ left: `${String(dragSpan.left)}%`, width: `${String(dragSpan.width)}%` }}
            data-testid="planning-schedule-drag-ghost"
            data-start={new Date(dragging.targetStartMs).toISOString()}
            aria-hidden="true"
          >
            <span className="planning-block-title">
              {t("planning.schedule.move.dragTo", {
                time: zonedStamp(
                  new Date(dragging.targetStartMs).toISOString(),
                  i18n.language,
                  timezone,
                ),
                wc: lane.code,
              })}
            </span>
          </div>
        ) : null}
      </div>
      {unplaced.length > 0 ? (
        <div className="planning-lane-unplaced" data-testid="planning-schedule-unplaced">
          <span className="planning-warn">
            {t("planning.schedule.lane.unplacedTitle", { count: unplaced.length })}
          </span>
          <ul className="planning-unplaced-list">
            {unplaced.map((op) => (
              <li key={op.op_ref} data-testid="planning-schedule-unplaced-op" {...opData(op)}>
                <button
                  type="button"
                  className="planning-linklike"
                  onClick={() => {
                    onOpen(op);
                  }}
                >
                  {op.label.order_no ?? t("planning.schedule.block.plannedOrder")} · {op.step_name}
                </button>
                <UnplacedReasonText reason={op.unplaced_reason} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function Block({
  op,
  startMs,
  endMs,
  span,
  row,
  lane,
  timezone,
  onOpen,
  pointer,
  dragged,
  moving,
}: {
  op: ScheduleOperation;
  startMs: number;
  endMs: number;
  span: { left: number; width: number };
  row: number;
  lane: ScheduleLane;
  timezone: string;
  onOpen: (op: ScheduleOperation) => void;
  pointer: BlockPointer | undefined;
  dragged: boolean;
  moving: boolean;
}) {
  const { t, i18n } = useTranslation();
  const total = endMs - startMs;
  const gaps = total > 0 ? closedGaps(startMs, endMs, lane.pieces) : [];
  const work = op.setup_minutes + op.run_minutes;
  const lead = work > 0 && op.setup_minutes > 0 ? (op.setup_minutes / work) * 100 : 0;
  const order = op.label.order_no ?? t("planning.schedule.block.plannedOrder");
  const qty = `${formatExactQty(op.qty, i18n.language)} ${op.uom}`;
  const classes = ["planning-block"];
  classes.push(op.source_kind === "wo_operation" ? "is-wo" : "is-po");
  if (op.is_late) classes.push("is-late");
  if (op.batch_key !== null) classes.push("is-batch");
  const blocked = pointer !== undefined ? moveBlock(op) : undefined;
  if (pointer !== undefined && blocked === undefined) classes.push("is-draggable");
  if (dragged) classes.push("is-dragging");
  if (moving) classes.push("is-moving");
  return (
    <button
      type="button"
      className={classes.join(" ")}
      data-draggable={pointer === undefined ? "" : String(blocked === undefined)}
      title={blocked !== undefined ? t(`planning.schedule.move.blocked.${blocked}`) : undefined}
      {...(pointer !== undefined
        ? {
            onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
              pointer.down(event, op, startMs, endMs);
            },
            onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
              pointer.move(event);
            },
            onPointerUp: () => {
              pointer.up();
            },
            onPointerCancel: () => {
              pointer.cancel();
            },
          }
        : {})}
      style={{
        left: `${String(span.left)}%`,
        width: `${String(span.width)}%`,
        top: `${String(row * 46 + 5)}px`,
      }}
      data-testid="planning-schedule-block"
      {...opData(op)}
      aria-label={t("planning.schedule.block.aria", {
        order,
        step: op.step_name,
        start: zonedStamp(op.planned_start, i18n.language, timezone),
        end: zonedStamp(op.planned_end, i18n.language, timezone),
      })}
      onClick={() => {
        onOpen(op);
      }}
    >
      {op.is_late ? <Hatch left={0} width={100} late className="planning-block-late" /> : null}
      {gaps.map((gap) => (
        <Hatch
          key={gap.start}
          left={((gap.start - startMs) / total) * 100}
          width={((gap.end - gap.start) / total) * 100}
          className="planning-hatched planning-block-gap"
        />
      ))}
      {lead > 0 ? (
        <span
          className="planning-block-setup"
          style={{ width: `${String(Math.min(100, lead))}%` }}
          data-testid="planning-schedule-block-setup"
          data-minutes={op.setup_minutes}
          title={t("planning.schedule.block.setup", { minutes: op.setup_minutes })}
        >
          {t("planning.schedule.block.setupShort", { minutes: op.setup_minutes })}
        </span>
      ) : null}
      <span className="planning-block-body">
        <span className="planning-block-title">
          <OperationMarks op={op} compact />
          <span className="planning-block-order">{order}</span>
        </span>
        <span className="planning-block-sub">
          {op.step_name} · {qty}
        </span>
        {op.batch_key !== null && op.setup_key !== null ? (
          <span className="planning-block-key" data-testid="planning-schedule-block-key">
            {keyText(op.setup_key)}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * The board at 412 px (§4.6): the chosen lane's operations on the chosen day
 * as cards in sequence, each with its times, order, step, quantity, chips and
 * changeover minutes. It reads only; nothing is dragged.
 */
export function ScheduleCards({
  ops,
  unplaced,
  timezone,
  onOpen,
}: {
  ops: readonly ScheduleOperation[];
  unplaced: readonly ScheduleOperation[];
  timezone: string;
  onOpen: (op: ScheduleOperation) => void;
}) {
  const { t, i18n } = useTranslation();
  const sorted = [...ops].sort((a, b) => {
    const sa = a.sequence_index ?? Number.MAX_SAFE_INTEGER;
    const sb = b.sequence_index ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    return (a.planned_start ?? "") < (b.planned_start ?? "") ? -1 : 1;
  });
  return (
    <div className="planning-schedule-cards">
      {sorted.length === 0 ? (
        <p className="planning-note" data-testid="planning-schedule-phone-none">
          {t("planning.schedule.phone.none")}
        </p>
      ) : null}
      {sorted.map((op) => (
        <button
          key={op.op_ref}
          type="button"
          className={op.is_late ? "planning-op-card is-late" : "planning-op-card"}
          data-testid="planning-schedule-card"
          {...opData(op)}
          onClick={() => {
            onOpen(op);
          }}
        >
          <span className="planning-op-card-times">
            {t("planning.schedule.times", {
              start: zonedStamp(op.planned_start, i18n.language, timezone, { date: false }),
              end: zonedStamp(op.planned_end, i18n.language, timezone),
            })}
          </span>
          <span className="planning-op-card-title">
            {op.label.order_no ?? t("planning.schedule.block.plannedOrder")} · {op.step_name}
          </span>
          <span className="planning-op-card-sub">
            {formatExactQty(op.qty, i18n.language)} {op.uom}
            {op.setup_minutes > 0
              ? ` · ${t("planning.schedule.block.setup", { minutes: op.setup_minutes })}`
              : ""}
          </span>
          {op.setup_key !== null ? (
            <span className="planning-op-card-sub">{keyText(op.setup_key)}</span>
          ) : null}
          <OperationMarks op={op} />
        </button>
      ))}
      {unplaced.length > 0 ? (
        <div className="planning-lane-unplaced" data-testid="planning-schedule-unplaced">
          <span className="planning-warn">
            {t("planning.schedule.lane.unplacedTitle", { count: unplaced.length })}
          </span>
          <ul className="planning-unplaced-list">
            {unplaced.map((op) => (
              <li key={op.op_ref} data-testid="planning-schedule-unplaced-op" {...opData(op)}>
                <span>
                  {op.label.order_no ?? t("planning.schedule.block.plannedOrder")} · {op.step_name}
                </span>
                <UnplacedReasonText reason={op.unplaced_reason} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
